/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office */

// Configuration du serveur MCP
const MCP_SERVER_URL = "http://localhost:5000";

// Configuration SharePoint
const SHAREPOINT_DELIVERY = {
  type: "sharepoint",
  sharePointSiteUrl: "https://alcyoneconsultinglux.sharepoint.com/sites/CMDeveloppement",
  sharePointLibrary: "Entrees",
  sharePointFolder: "",
};

let currentAttachments = [];
const MAGIC = {
  PDF:  ["25 50 44 46 2D"],
  OLE:  ["D0 CF 11 E0 A1 B1 1A E1"],
  ZIP:  ["50 4B 03 04", "50 4B 05 06", "50 4B 07 08"],
  PNG:  ["89 50 4E 47"],
  JPEG: ["FF D8 FF"],
  GIF:  ["47 49 46 38"],
  BMP:  ["42 4D"],
  TIFF: ["49 49 2A 00", "4D 4D 00 2A"],
  WEBP: ["52 49 46 46"]
};

const ALLOWED_EXT = new Set([
  "pdf", "doc", "docx", "xls", "xlsx",
  "png", "jpg", "jpeg", "gif", "bmp", "tiff", "tif", "webp"
]);
const BLOCKED_EXT = new Set(["docm", "dotm", "xlsm"]);
const ALLOWED_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png", "image/jpeg", "image/gif", "image/bmp", "image/tiff", "image/webp"
]);

Office.onReady((info) => {
  const t = Office.context?.officeTheme;
  if (t?.bodyBackgroundColor) {
    document.documentElement.style.setProperty("--cm-bg", t.bodyBackgroundColor);
  }
  if (t?.bodyForegroundColor) {
    document.documentElement.style.setProperty("--cm-text", t.bodyForegroundColor);
  }

  if (info.host === Office.HostType.Outlook) {
    document.getElementById("loadAttachments").onclick = loadAttachmentList;
    document.getElementById("sendSelected").onclick = sendSelected;
  }
});

// ── Validation magic bytes ──────────────────────────────────────────────────

async function validateAttachment(att) {
  const name = (att?.name || "").trim();
  const lower = name.toLowerCase();
  const ext = lower.includes(".") ? lower.split(".").pop() : "";

  if (BLOCKED_EXT.has(ext)) {
    return { ok: false, reason: `Extension macro interdite: .${ext}` };
  }

  if (!ALLOWED_EXT.has(ext)) {
    return { ok: false, reason: `Extension non autorisée: .${ext || "?"}` };
  }

  if (att.contentType &&
      att.contentType !== "application/octet-stream" &&
      !ALLOWED_TYPES.has(att.contentType)) {
    return { ok: false, reason: `MIME non autorisé: ${att.contentType}` };
  }

  const head = await getAttachmentHeadBytes(att.id, 8);
  const sig2 = toHex(head.slice(0, 2));
  const sig3 = toHex(head.slice(0, 3));
  const sig4 = toHex(head.slice(0, 4));
  const sig5 = toHex(head.slice(0, 5));
  const sig8 = toHex(head.slice(0, 8));

  // Documents
  if (ext === "pdf") {
    if (!MAGIC.PDF.includes(sig5)) {
      return { ok: false, reason: "Signature PDF invalide (%PDF- manquant)" };
    }
    return { ok: true };
  }

  if (ext === "doc" || ext === "xls") {
    if (!MAGIC.OLE.includes(sig8)) {
      return { ok: false, reason: `Signature ${ext.toUpperCase()} (OLE) invalide` };
    }
    return { ok: true };
  }

  if (ext === "docx" || ext === "xlsx") {
    if (!MAGIC.ZIP.includes(sig4)) {
      return { ok: false, reason: `Signature ${ext.toUpperCase()} invalide (ZIP attendu)` };
    }
    return { ok: true };
  }

  // Images
  if (ext === "png") {
    if (!MAGIC.PNG.includes(sig4)) {
      return { ok: false, reason: "Signature PNG invalide" };
    }
    return { ok: true };
  }

  if (ext === "jpg" || ext === "jpeg") {
    if (!MAGIC.JPEG.includes(sig3)) {
      return { ok: false, reason: "Signature JPEG invalide" };
    }
    return { ok: true };
  }

  if (ext === "gif") {
    if (!MAGIC.GIF.includes(sig4)) {
      return { ok: false, reason: "Signature GIF invalide" };
    }
    return { ok: true };
  }

  if (ext === "bmp") {
    if (!MAGIC.BMP.includes(sig2)) {
      return { ok: false, reason: "Signature BMP invalide" };
    }
    return { ok: true };
  }

  if (ext === "tiff" || ext === "tif") {
    if (!MAGIC.TIFF.includes(sig4)) {
      return { ok: false, reason: "Signature TIFF invalide" };
    }
    return { ok: true };
  }

  if (ext === "webp") {
    if (!MAGIC.WEBP.includes(sig4)) {
      return { ok: false, reason: "Signature WEBP invalide" };
    }
    return { ok: true };
  }

  return { ok: false, reason: "Type non supporté" };
}

function getAttachmentHeadBytes(attachmentId, nBytes = 8) {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAttachmentContentAsync(attachmentId, async (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(result.error);
        return;
      }

      const { content, format } = result.value;

      if (format === Office.MailboxEnums.AttachmentContentFormat.Base64 || format === "base64") {
        resolve(base64HeadToUint8Array(content, nBytes));
        return;
      }

      if (format === Office.MailboxEnums.AttachmentContentFormat.Url || format === "url") {
        try {
          const head = await fetchHeadBytes(content, nBytes);
          resolve(head);
        } catch (e) {
          reject(e);
        }
        return;
      }

      reject(new Error("Format de pièce jointe non supporté: " + format));
    });
  });
}

function base64HeadToUint8Array(base64, nBytes) {
  const neededChars = Math.ceil(nBytes / 3) * 4;
  const chunk = base64.slice(0, neededChars);

  const binary = atob(chunk);
  const bytes = new Uint8Array(Math.min(binary.length, nBytes));
  for (let i = 0; i < bytes.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function fetchHeadBytes(url, nBytes) {
  const res = await fetch(url, { headers: { Range: `bytes=0-${nBytes - 1}` } });
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf).slice(0, nBytes);
}

function toHex(uint8) {
  return Array.from(uint8)
    .map(b => b.toString(16).padStart(2, "0").toUpperCase())
    .join(" ");
}

// ── Lecture contenu pièce jointe ────────────────────────────────────────────

function getAttachmentContentAsync(attachmentId) {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAttachmentContentAsync(attachmentId, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message || "Impossible de lire la pièce jointe"));
      }
    });
  });
}

// Décode une chaîne base64 en Blob binaire pour l'envoi multipart. Le découpage en
// tranches évite d'allouer un unique tableau géant pour les grosses pièces jointes.
function base64ToBlob(base64, mimeType) {
  const byteChars = atob(base64);
  const sliceSize = 8192;
  const byteArrays = [];
  for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
    const slice = byteChars.slice(offset, offset + sliceSize);
    const byteNumbers = new Uint8Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(byteNumbers);
  }
  return new Blob(byteArrays, { type: mimeType || "application/octet-stream" });
}

function getMimeType(fileName) {
  const name = (fileName || "").toLowerCase();
  if (name.endsWith(".pdf"))  return "application/pdf";
  if (name.endsWith(".doc"))  return "application/msword";
  if (name.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (name.endsWith(".xls"))  return "application/vnd.ms-excel";
  if (name.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (name.endsWith(".png"))  return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".gif"))  return "image/gif";
  if (name.endsWith(".bmp"))  return "image/bmp";
  if (name.endsWith(".tiff") || name.endsWith(".tif")) return "image/tiff";
  if (name.endsWith(".webp")) return "image/webp";
  return "application/octet-stream";
}

// ── Chargement des pièces jointes ───────────────────────────────────────────

function loadAttachmentList() {
  const status = el("status");
  const list = el("attachment-list");
  const sendBtn = el("sendSelected");

  status.innerHTML = spinner("Chargement des pièces jointes...");
  list.innerHTML = "";
  sendBtn.style.display = "none";
  currentAttachments = [];

  const item = Office.context.mailbox.item;

  if (!item?.attachments?.length) {
    status.innerHTML = errorMsg("Aucune pièce jointe");
    return;
  }

  const docs = item.attachments.filter(isAllowedAttachment);

  if (!docs.length) {
    status.innerHTML = errorMsg("Aucun fichier compatible trouvé");
    return;
  }

  currentAttachments = docs;

  status.innerHTML = infoMsg(`${docs.length} fichier(s) trouvé(s). Sélectionnez ceux à envoyer.`);
  sendBtn.style.display = "flex";

  list.innerHTML = `
      <div class="cm-list-head">
        <div class="cm-list-title">Fichiers</div>
        <div class="cm-list-actions">
          <a href="#" id="checkAll" class="cm-link">Tout cocher</a>
          <a href="#" id="uncheckAll" class="cm-link">Tout décocher</a>
        </div>
      </div>
      <div id="items" class="cm-card"></div>
    `;

  const items = document.getElementById("items");

  docs.forEach((att, idx) => {
    const name = escapeHtml(att.name || `fichier-${idx + 1}`);
    const type = escapeHtml(friendlyType(att));

    items.insertAdjacentHTML("beforeend", `
      <label class="cm-item">
        <input type="checkbox" class="att-check" data-id="${att.id}" checked />
        <div style="flex:1; min-width:0;">
          <div class="cm-item-name">${name}</div>
          <div class="cm-item-type">${type}</div>
        </div>
        <span class="cm-badge">${type}</span>
      </label>
    `);
  });

  document.getElementById("checkAll").onclick = (e) => { e.preventDefault(); setAll(true); };
  document.getElementById("uncheckAll").onclick = (e) => { e.preventDefault(); setAll(false); };
}

// ── Envoi et conversion ─────────────────────────────────────────────────────

async function sendSelected() {
  const status = el("status");
  const sendBtn = el("sendSelected");
  const selected = getSelectedAttachments();

  if (!selected.length) {
    status.innerHTML = errorMsg("Aucun fichier sélectionné");
    return;
  }

  sendBtn.disabled = true;

  // Étape 1 : Validation des signatures (magic bytes)
  status.innerHTML = spinner("Vérification des signatures...");

  for (const att of selected) {
    let res;
    try {
      res = await validateAttachment(att);
    } catch (e) {
      console.error(e);
      status.innerHTML = errorMsg(`Erreur lecture pièce jointe: ${att.name}`);
      sendBtn.disabled = false;
      return;
    }

    if (!res.ok) {
      status.innerHTML = errorMsg(`Rejeté: ${att.name} -- ${res.reason}`);
      sendBtn.disabled = false;
      return;
    }
  }

  // Étape 2 : Lecture du contenu des pièces jointes
  try {
    const formData = new FormData();
    let firstFileName = null;
    for (const att of selected) {
      status.innerHTML = spinner(`Lecture de ${escapeHtml(att.name)}...`);
      const content = await getAttachmentContentAsync(att.id);

      const mimeType = att.contentType || getMimeType(att.name);
      // Décoder le base64 en binaire : envoi multipart, sans plafond serveur ni surcoût +33 %.
      const blob = base64ToBlob(content.content, mimeType);
      formData.append("files", blob, att.name);
      if (firstFileName === null) firstFileName = att.name;
    }

    // Étape 3 : Envoi vers SharePoint via le serveur MCP (multipart streaming)
    status.innerHTML = spinner("Envoi vers SharePoint...");

    const delivery = {
      ...SHAREPOINT_DELIVERY,
      sharePointFileName: firstFileName,
    };
    formData.append("delivery", JSON.stringify(delivery));

    // Endpoint streaming : les fichiers transitent en binaire, ce qui lève la limite
    // ~124 Mo/fichier de l'ancien endpoint JSON base64 (/api/convert).
    let response;
    try {
      response = await fetch(`${MCP_SERVER_URL}/api/convert/stream`, {
        method: "POST",
        body: formData
      });
    } catch (networkError) {
      console.error(networkError);
      throw new Error("serveur GESDO injoignable. Vérifiez votre connexion réseau, puis réessayez.");
    }

    // Une panne d'infrastructure (proxy, 502) renvoie du HTML : sans ce garde,
    // l'utilisateur verrait une erreur de parsing JSON au lieu d'un message utile.
    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error(`réponse inattendue du serveur (HTTP ${response.status}). Contactez l'administrateur.`);
    }

    if (!response.ok || !result.success) {
      throw new Error(result.error || `le serveur a refusé l'envoi (HTTP ${response.status}).`);
    }

    // Étape 4 : Affichage du résultat avec lien direct
    const fileName = delivery.sharePointFileName;
    const folder = delivery.sharePointFolder ? delivery.sharePointFolder + "/" : "";
    const directUrl = `${delivery.sharePointSiteUrl}/${delivery.sharePointLibrary}/${folder}${encodeURIComponent(fileName)}`;

    status.innerHTML = successMsg(`Envoi reussi ! ${selected.length} fichier(s) envoye(s) vers SharePoint.`);
    status.insertAdjacentHTML("beforeend", `
      <div style="margin-top: 10px;">
        <a href="${escapeHtml(directUrl)}" target="_blank" class="cm-link" style="font-weight: 600;">
          <i class="ms-Icon ms-Icon--OpenInNewTab" aria-hidden="true"></i> Ouvrir sur SharePoint
        </a>
      </div>
    `);

  } catch (error) {
    console.error("Erreur lors de l'envoi:", error);
    status.innerHTML = errorMsg(`Échec de l'envoi : ${error.message}`);
  } finally {
    sendBtn.disabled = false;
  }
}

// ── Utilitaires ─────────────────────────────────────────────────────────────

function setAll(checked) {
  document.querySelectorAll(".att-check").forEach(cb => cb.checked = checked);
}

function getSelectedAttachments() {
  const selectedIds = Array.from(document.querySelectorAll(".att-check"))
    .filter(cb => cb.checked)
    .map(cb => cb.getAttribute("data-id"));

  return currentAttachments.filter(att => selectedIds.includes(att.id));
}

function isAllowedAttachment(att) {
  if (!att) return false;
  if (att.contentType && ALLOWED_TYPES.has(att.contentType)) return true;

  const name = (att.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  return ALLOWED_EXT.has(ext);
}

function friendlyType(att) {
  const name = (att?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";

  const labels = {
    pdf: "PDF", doc: "DOC", docx: "DOCX",
    xls: "XLS", xlsx: "XLSX",
    png: "PNG", jpg: "JPG", jpeg: "JPEG",
    gif: "GIF", bmp: "BMP",
    tiff: "TIFF", tif: "TIF", webp: "WEBP"
  };

  return labels[ext] || att?.contentType || "Fichier";
}

function el(id) { return document.getElementById(id); }

function escapeHtml(str) {
  return String(str)
    .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;").replaceAll("'", "&#039;");
}

function spinner(text) {
  return `<span class="cm-status-info"><i class="ms-Icon ms-Icon--Sync" aria-hidden="true"></i> ${escapeHtml(text)}</span>`;
}
function errorMsg(text) {
  return `<span class="cm-status-error">${escapeHtml(text)}</span>`;
}
function successMsg(text) {
  return `<span class="cm-status-success">${escapeHtml(text)}</span>`;
}
function infoMsg(text) {
  return `<span class="cm-status-info">${escapeHtml(text)}</span>`;
}
